import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import i18n from "../../i18n/config";
import { API_URL, getAuthHeaders } from "../../lib/utils";

const BASE = `${API_URL}/visitors`;

// Pequeño cliente para el módulo de visitas
export const visitorApi = {
  get: (path, params) =>
    axios.get(`${BASE}${path}`, { headers: getAuthHeaders(), params: cleanParams(params) }).then((r) => r.data),
  post: (path, data, params) =>
    axios.post(`${BASE}${path}`, data ?? {}, { headers: getAuthHeaders(), params: cleanParams(params) }).then((r) => r.data),
  put: (path, data, params) =>
    axios.put(`${BASE}${path}`, data ?? {}, { headers: getAuthHeaders(), params: cleanParams(params) }).then((r) => r.data),
};

const cleanParams = (params) => {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "" && v !== "all")
  );
};

export const tzOffset = () => new Date().getTimezoneOffset();

export const fmtDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" });
};

export const fmtTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
};

export const fmtDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")} min` : `${m} min`;
};

// Convierte un <input type="datetime-local"> a ISO UTC
export const localInputToIso = (value) => (value ? new Date(value).toISOString() : null);

// Valor para <input type="datetime-local"> a partir de una fecha
export const toLocalInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Rango [inicio, fin) en ISO para fechas YYYY-MM-DD locales
export const dayRangeIso = (fromDay, toDay) => ({
  date_from: fromDay ? new Date(`${fromDay}T00:00:00`).toISOString() : undefined,
  date_to: toDay ? new Date(new Date(`${toDay}T00:00:00`).getTime() + 86400000).toISOString() : undefined,
});

export const todayInput = () => toLocalInput(new Date()).slice(0, 10);

/**
 * Exporta filas a Excel.
 * columns: [{ header, value: (row) => any, width? }]
 */
export const exportToExcel = (rows, columns, filename, title) => {
  const header = columns.map((c) => c.header);
  const data = rows.map((r) => columns.map((c) => c.value(r) ?? ""));
  const aoa = title ? [[title], [], header, ...data] : [header, ...data];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = columns.map((c) => ({ wch: c.width || 18 }));
  const headerRow = title ? 2 : 0;
  columns.forEach((_, i) => {
    const cell = ws[XLSX.utils.encode_cell({ r: headerRow, c: i })];
    if (cell) {
      cell.s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "0F172A" } },
        alignment: { horizontal: "center" },
      };
    }
  });
  if (title && ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 14 } };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Exporta filas a PDF (horizontal) con tabla.
 * sections: [{ title, columns, rows }] adicionales opcionales (resúmenes)
 */
export const exportToPdf = ({ title, subtitle, columns, rows, summary, filename }) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(16);
  doc.text(title, 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(100);
  if (subtitle) doc.text(subtitle, 40, 56);
  doc.text(`Sentinel Pro · ${new Date().toLocaleString(i18n.language)}`, 40, subtitle ? 68 : 56);
  let startY = subtitle ? 82 : 70;
  if (summary?.length) {
    autoTable(doc, {
      startY,
      body: summary.map(([k, v]) => [k, String(v ?? "—")]),
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 180 } },
      tableWidth: 360,
    });
    startY = doc.lastAutoTable.finalY + 12;
  }
  autoTable(doc, {
    startY,
    head: [columns.map((c) => c.header)],
    body: rows.map((r) => columns.map((c) => String(c.value(r) ?? ""))),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [15, 23, 42] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });
  doc.save(`${filename}.pdf`);
};

// Columnas comunes para exportar listados de visitas
export const visitExportColumns = (t) => [
  { header: t("visitors.common.visitor"), value: (v) => v.visitor_name, width: 26 },
  { header: t("visitors.common.idNumber"), value: (v) => v.visitor_id_number },
  { header: t("visitors.common.visitorCompany"), value: (v) => v.visitor_company },
  { header: t("visitors.common.facility"), value: (v) => v.facility_name, width: 22 },
  { header: t("visitors.common.host"), value: (v) => v.host_name, width: 22 },
  { header: t("visitors.common.purpose"), value: (v) => v.purpose, width: 22 },
  { header: t("visitors.common.checkIn"), value: (v) => fmtDateTime(v.check_in_at) },
  { header: t("visitors.common.checkOut"), value: (v) => fmtDateTime(v.check_out_at) },
  { header: t("visitors.common.duration"), value: (v) => fmtDuration(v.duration_minutes) },
  { header: t("visitors.common.plate"), value: (v) => v.vehicle?.plate },
  { header: t("visitors.common.guard"), value: (v) => v.check_in_by_name },
  { header: t("visitors.common.status"), value: (v) => t(`visitors.status.${v.status}`) },
];
